import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBuilding,
  FaCheckCircle,
  FaEnvelope,
  FaHome,
  FaKey,
  FaLock,
  FaShieldAlt,
} from "react-icons/fa";
import PasswordResetModal from "./PasswordResetModal";
import { useAppTheme } from "../context/AppThemeContext";

const renderActionContent = (action, iconClass, mutedClass) => {
  const Icon = action.icon || FaHome;

  return (
    <>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm ${iconClass}`}>
        <Icon />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold">{action.title}</span>
        <span className={`mt-0.5 block text-xs leading-5 ${mutedClass}`}>{action.description}</span>
      </span>
      <FaArrowRight className="ml-auto shrink-0 text-xs text-blue-500" />
    </>
  );
};

export default function ManagementProfilePage({
  displayName,
  initials,
  email,
  roleLabel,
  dashboardPath,
  primaryName,
  organizationName,
  roleFocus,
  profileLoading,
  accountDetails,
  quickActions,
  showResetForm,
  setShowResetForm,
  newPassword,
  setNewPassword,
  isSubmitting,
  handleResetPassword,
  handleLogout,
  isLoggingOut = false,
}) {
  const { isDarkMode } = useAppTheme();

  const pageClass = isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900";
  const cardClass = isDarkMode
    ? "border-slate-800 bg-slate-900 shadow-slate-950/20"
    : "border-slate-200 bg-white shadow-slate-200/60";
  const mutedClass = isDarkMode ? "text-slate-400" : "text-slate-500";
  const subtleClass = isDarkMode ? "text-slate-300" : "text-slate-600";
  const detailClass = isDarkMode
    ? "border-slate-800 bg-slate-950/60"
    : "border-slate-200 bg-slate-50";
  const actionClass = isDarkMode
    ? "border-slate-800 hover:bg-slate-800/60"
    : "border-slate-200 hover:bg-slate-50";
  const iconClass = isDarkMode ? "bg-blue-950 text-blue-300" : "bg-blue-50 text-blue-600";

  const coreDetails = [
    { label: "Role", value: roleLabel },
    { label: "Focus Area", value: roleFocus },
    { label: "Status", value: profileLoading ? "Refreshing" : "Active" },
    ...(accountDetails || []),
  ];

  const defaultActions = [
    {
      title: "Open Dashboard",
      description: "Return to the management workspace.",
      icon: FaHome,
      kind: "link",
      href: dashboardPath,
    },
    {
      title: "Reset Password",
      description: "Update your sign-in credentials securely.",
      icon: FaKey,
      kind: "button",
      onClick: () => setShowResetForm(true),
    },
  ];
  const visibleActions = quickActions?.length ? quickActions : defaultActions;

  return (
    <div className={`min-h-full px-4 py-6 md:px-6 ${pageClass}`}>
      <div className="mx-auto max-w-6xl space-y-6">
        <section className={`overflow-hidden rounded-xl border shadow-sm ${cardClass}`}>
          <div className="h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-500" />
          <div className="flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xl font-bold text-white shadow-sm dark:bg-blue-600">
                {initials || "MP"}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Account profile</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">{primaryName || displayName}</h1>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-bold">
                  <span className={`rounded-md border px-3 py-1.5 ${detailClass}`}>{roleLabel}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-700">
                    <FaCheckCircle /> {profileLoading ? "Refreshing" : "Active"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowResetForm(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700"
              >
                <FaKey className="text-xs" /> Reset password
              </button>
              <Link
                to={dashboardPath}
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-bold transition ${isDarkMode ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              >
                <FaHome className="text-xs" /> Dashboard
              </Link>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.5fr_0.8fr]">
          <div className="space-y-6">
            <div className={`border-b px-1 pb-7 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                <div>
                  <p className={`text-xs font-bold uppercase tracking-[0.2em] ${mutedClass}`}>Account overview</p>
                  <h2 className="mt-2 text-2xl font-black">Workspace identity</h2>
                </div>
                <span className={`inline-flex w-fit items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-bold ${
                  isDarkMode ? "border-slate-700 bg-slate-900 text-teal-300" : "border-teal-200 bg-teal-50 text-teal-700"
                }`}>
                  <FaShieldAlt /> Secured account
                </span>
              </div>

              <div className={`mt-6 grid gap-0 border-y sm:grid-cols-2 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                <div className={`py-5 sm:pr-6 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                  <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] ${mutedClass}`}>
                    <FaEnvelope /> Primary email
                  </div>
                  <p className="mt-3 break-all text-sm font-bold">{email || "Not available"}</p>
                </div>
                <div className={`border-t py-5 sm:border-l sm:border-t-0 sm:pl-6 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                  <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] ${mutedClass}`}>
                    <FaBuilding /> Organization
                  </div>
                  <p className="mt-3 text-sm font-bold">{organizationName || "Not available"}</p>
                </div>
              </div>
            </div>

            <div className="px-1 pt-1">
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${mutedClass}`}>Account details</p>
              <h2 className="mt-2 text-2xl font-black">Access and subscription</h2>
              <dl className={`mt-6 divide-y border-y ${isDarkMode ? "divide-slate-800 border-slate-800" : "divide-slate-200 border-slate-200"}`}>
                {coreDetails.map((detail) => (
                  <div key={detail.label} className="grid gap-1 py-4 sm:grid-cols-[12rem_1fr] sm:items-center sm:gap-6">
                    <dt className={`text-xs font-semibold uppercase tracking-[0.12em] ${mutedClass}`}>{detail.label}</dt>
                    <dd className="text-sm font-semibold leading-5">{detail.value || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <aside className="space-y-6">
            <div className={`rounded-xl border p-5 shadow-sm ${cardClass}`}>
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${mutedClass}`}>Quick actions</p>
              <h2 className="mt-2 text-xl font-black">Next steps</h2>
              <div className="mt-5 space-y-3">
                {visibleActions.map((action) => {
                  const content = renderActionContent(action, iconClass, mutedClass);
                  const className = `group flex w-full items-center gap-3 border-b px-1 py-4 text-left transition last:border-b-0 ${actionClass}`;

                  if (action.kind === "button") {
                    return (
                      <button key={action.title} type="button" onClick={action.onClick} className={className}>
                        {content}
                      </button>
                    );
                  }

                  if (action.kind === "external") {
                    return (
                      <a key={action.title} href={action.href} className={className}>
                        {content}
                      </a>
                    );
                  }

                  return (
                    <Link key={action.title} to={action.href} className={className}>
                      {content}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className={`rounded-xl border p-5 shadow-sm ${cardClass}`}>
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${mutedClass}`}>Security</p>
              <h2 className="mt-2 text-xl font-black">Password controls</h2>
              <p className={`mt-3 text-sm leading-6 ${subtleClass}`}>
                Keep privileged account access protected and rotate credentials when access changes.
              </p>
              <ul className={`mt-4 space-y-3 text-sm ${subtleClass}`}>
                <li className="flex gap-2"><FaCheckCircle className="mt-1 text-teal-500" /> Password protected</li>
                <li className="flex gap-2"><FaLock className="mt-1 text-blue-500" /> Session cleared after reset</li>
              </ul>
              <button
                type="button"
                onClick={() => setShowResetForm(true)}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700"
              >
                <FaKey className="text-xs" /> Change password
              </button>
              {handleLogout && (
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className={`mt-3 inline-flex w-full items-center justify-center rounded-lg border px-4 py-2.5 text-sm font-bold transition ${
                    isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  } disabled:cursor-wait disabled:opacity-70`}
                >
                  {isLoggingOut ? <span className="inline-flex items-center gap-2"><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" /> Logging out...</span> : "Sign out"}
                </button>
              )}
            </div>
          </aside>
        </section>
      </div>

      <PasswordResetModal
        isOpen={showResetForm}
        title={`Reset ${roleLabel ? roleLabel.toLowerCase() : ""} password`}
        description="Update the password for this account. Use the new credential the next time you sign in."
        email={email}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        isSubmitting={isSubmitting}
        onClose={() => setShowResetForm(false)}
        onSubmit={handleResetPassword}
      />
    </div>
  );
}
